/* ===================================================================
   OnboardingOverlay -- the first-run 6-step guided checklist, mounted as a
   portal in AppShell so it covers ANY screen.

   Faithful port of the prototype's `showOnboarding` modal
   ("Rhody Reviews.dc.html" 138-186): a centered card with a blue gradient
   header (glyph, "Welcome to Rhody Reviews", progress bar + "N of M
   complete"), a list of steps (done / current / upcoming states with the
   current step's CTA), an "all set" celebration when complete, and an
   "I'll finish later" footer. Structure, colors and copy lifted verbatim.

   RECONCILED WITH EXISTING FLOWS:
     - The full multi-step wizard (industry gate, CSV import, brand voice
       persistence, dry-run first requests) already lives at routes/Onboarding
       .tsx (the /onboarding route). This overlay is the lightweight nudge
       version from the prototype: each step's CTA routes into the real screen
       (or the /onboarding wizard) where the actual work happens. We do NOT
       duplicate the wizard's logic.
     - Step done/current state is DERIVED from useSetupProgress() (the same
       real GBP/customers/voice/requests signals FirstRun + the setup card
       use). The prototype's 6-row checklist maps onto the 4 verifiable
       signals; the two rows with no fabrication-free signal (confirm-profile,
       setup-sending) are folded in rather than faked.

   GATING (HANDOFF section 6.7: show first-run ONLY for brand-new accounts):
     - Auto-opens once when the account is brand new (nothing set up yet) and
       the first-run overlay has not been seen on this browser (per-client
       localStorage flag). Dismissing sets the flag so it never re-nags.
     - Also opens on demand via the window CustomEvent 'rhody:open-onboarding'
       (dispatched by the Finish-setup card's "Resume setup"), regardless of
       the seen flag.
   =================================================================== */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMe, useSetupProgress } from '../../hooks';
import { useLocalFlag } from '../../hooks/useLocalFlag';
import '../../styles/overlays.css';

export function OnboardingOverlay() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const progress = useSetupProgress();
  const [seen, setSeen] = useLocalFlag('onboarding-seen', me?.client_id);
  const [open, setOpen] = useState(false);

  // On-demand open from the Finish-setup card (and anywhere else that wants
  // to resume setup). Always honored, even after the first-run auto-show.
  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener('rhody:open-onboarding', openIt);
    return () => window.removeEventListener('rhody:open-onboarding', openIt);
  }, []);

  // AUTO-OPEN RETIRED (Phase 3, 2026-07-07). The unified checklist home
  // (routes/Onboarding at /onboarding, and its embedded variant overlaid on a
  // returning zero-data dashboard) is now the ONE first-run surface. Auto-
  // popping this modal on top of it would double-surface setup, so brand-new
  // accounts are routed to /onboarding (PostAuthLanding) instead. This overlay
  // stays mounted only for the explicit on-demand event above (no current
  // dispatcher), and never auto-nags. `seen` is retained for that path.
  void seen;

  if (!open) return null;

  const bizName = me?.business_name ?? 'your business';
  const { steps, doneCount, total, pct, nextStep } = progress;

  function close() {
    setSeen(true); // never auto-nag again on this browser for this client
    setOpen(false);
  }

  function runStep(route: string) {
    close();
    navigate(route);
  }

  const overlay = (
    <div className="rh-onb" role="dialog" aria-modal="true" aria-label="Get set up">
      <div className="rh-onb__card">
        <div className="rh-onb__head">
          <div className="rh-onb__glyph" data-keepcolor="1" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="#fbbc04">
              <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
            </svg>
          </div>
          <div className="rh-onb__h">Welcome to Rhody Reviews</div>
          <div className="rh-onb__p">
            Let&apos;s get {bizName} set up. A few quick steps and reviews start flowing in
            &mdash; you can finish now or come back anytime.
          </div>
          <div className="rh-onb__progress">
            <div className="rh-onb__track">
              <div className="rh-onb__fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="rh-onb__pctlbl">
              {doneCount} of {total} complete
            </span>
          </div>
          <button className="rh-onb__x" aria-label="Close" onClick={close}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="rh-onb__list">
          {steps.filter((s) => s.core).map((s, i) => {
            const isCurrent = !s.done && s.id === nextStep?.id;
            const upcoming = !s.done && !isCurrent;
            return (
              <div
                key={s.id}
                className={
                  'rh-onb__step' +
                  (isCurrent ? ' rh-onb__step--current' : '') +
                  (upcoming ? ' rh-onb__step--upcoming' : '')
                }
              >
                <div className="rh-onb__marker">
                  {s.done ? (
                    <div className="rh-onb__badge rh-onb__badge--done">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                  ) : (
                    <div
                      className={
                        'rh-onb__badge ' +
                        (isCurrent ? 'rh-onb__badge--current' : 'rh-onb__badge--upcoming')
                      }
                    >
                      {i + 1}
                    </div>
                  )}
                </div>
                <div className="rh-onb__stepbody">
                  <div className="rh-onb__steptitle">{s.title}</div>
                  <div className="rh-onb__stepdesc">{s.desc}</div>
                  {isCurrent && (
                    <button className="rh-onb__cta" onClick={() => runStep(s.route)}>
                      {s.cta}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </button>
                  )}
                  {s.done && <div className="rh-onb__donetag">Done</div>}
                </div>
              </div>
            );
          })}

          {doneCount === total && (
            <div className="rh-onb__alldone">
              <div className="rh-onb__alldone-glyph" aria-hidden="true">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#34a853" strokeWidth={2.5}>
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <div className="rh-onb__alldone-h">You&apos;re all set!</div>
              <div className="rh-onb__alldone-p">
                {bizName} is live. Rhody will start collecting reviews after your next job.
              </div>
              <button className="rh-onb__alldone-btn" onClick={() => runStep('/dashboard')}>
                Go to my dashboard &rarr;
              </button>
            </div>
          )}

          <div className="rh-onb__foot">
            <button className="rh-onb__later" onClick={close}>
              I&apos;ll finish later
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
